import { useState, useEffect } from 'react';
import tr from './tr';
import en from './en';
import { LANGUAGE } from '../config';

const langs = { tr, en };

// Global dil state — tüm componentler aynı değeri okur
let currentLang = localStorage.getItem('language') || LANGUAGE || 'tr';
let listeners = [];

export const setLanguage = (lang) => {
    currentLang = lang;
    localStorage.setItem('language', lang);
    listeners.forEach(fn => fn(lang));
};

// Hook olmayan versiyon — component dışında, sub-componentlerde kullanılır
export const getLang = () => {
    const lang = localStorage.getItem('language') || LANGUAGE || 'tr';
    return langs[lang] || langs['tr'];
};

// Locale helper — tarih/saat ve sayı formatları için
export const getLocale = () => {
    const lang = localStorage.getItem('language') || LANGUAGE || 'tr';
    return lang === 'en' ? 'en-US' : 'tr-TR';
};

// ============================================================
// Tenant currency engine
//
// Default:
//   TR -> TRY
//   EN -> USD
//
// Tenant admin tarafından manuel para birimi seçilmişse
// currency_code backend/localStorage değerini korur.
// ============================================================

export const getAutomaticCurrencyForLanguage = (lang = null) => {
    const resolvedLang =
        lang ||
        localStorage.getItem('language') ||
        LANGUAGE ||
        'tr';

    return resolvedLang === 'en' ? 'USD' : 'TRY';
};

export const getCurrencyCode = () => {
    return (
        localStorage.getItem('currency_code') ||
        getAutomaticCurrencyForLanguage()
    ).toUpperCase();
};

export const getCurrencyLocale = () => {
    const code = getCurrencyCode();

    if (code === 'TRY') return 'tr-TR';
    if (code === 'USD') return 'en-US';
    if (code === 'EUR') return 'de-DE';

    return getLocale();
};

export const getCurrencySymbol = () => {
    const code = getCurrencyCode();

    if (code === 'TRY') return '₺';
    if (code === 'USD') return '$';
    if (code === 'EUR') return '€';

    return code;
};

export const isCurrencyExplicit = () => {
    return localStorage.getItem('currency_explicit') === 'true';
};

export const setTenantCurrencyState = ({
    currency_code,
    currency_explicit
} = {}) => {
    if (currency_code) {
        localStorage.setItem(
            'currency_code',
            String(currency_code).toUpperCase()
        );
    }

    if (currency_explicit !== undefined && currency_explicit !== null) {
        localStorage.setItem(
            'currency_explicit',
            String(Boolean(currency_explicit))
        );
    }
};

export const syncAutomaticCurrencyForLanguage = (lang) => {
    if (isCurrencyExplicit()) return getCurrencyCode();

    const currency = getAutomaticCurrencyForLanguage(lang);

    localStorage.setItem('currency_code', currency);
    localStorage.setItem('currency_explicit', 'false');

    return currency;
};

export const formatMoney = (
    value,
    {
        minimumFractionDigits = 2,
        maximumFractionDigits = 2,
        currencyDisplay = 'symbol'
    } = {}
) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
        return '—';
    }

    const currency = getCurrencyCode();

    try {
        return new Intl.NumberFormat(getCurrencyLocale(), {
            style: 'currency',
            currency,
            currencyDisplay,
            minimumFractionDigits,
            maximumFractionDigits
        }).format(numeric);
    } catch {
        return `${numeric.toLocaleString(getLocale(), {
            minimumFractionDigits,
            maximumFractionDigits
        })} ${getCurrencySymbol()}`;
    }
};

export const useLanguage = () => {
    const [lang, setLang] = useState(currentLang);

    useEffect(() => {
        const fn = (newLang) => setLang(newLang);
        listeners.push(fn);
        return () => { listeners = listeners.filter(l => l !== fn); };
    }, []);

    return { t: langs[lang] || langs['tr'], lang };
};

export default langs;
